import Icon from '@/components/Icon/Icon'
import Input from '@/components/Input'
import Checkbox from '@/components/Checkbox'
import Loader from '@/components/Loader/index'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

const InputDropdown = ({
  id,
  name,
  label,
  labelInfoText,
  variant,
  size,
  fullWidth = false,
  required,
  disabled,
  infoText,
  errorMessage,
  captionText,
  value,
  onChange,
  dropdownOption,
  dropdownSelectedOption,
  onSelectDropdownItem,
  multipleSelection = false,
  allowSelectAll,
  isLoading,
  showTooltipInfo = false,
  placeholder,
  dataTestId,
  style,
  disableSearch = false,
}) => {
  const [filteredOptions, setFilteredOptions] = useState(dropdownOption)
  const [isClearing, setIsClearing] = useState(false)
  const [chipValues, setChipValues] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const [noResults, setNoResults] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    if (dropdownOption?.length > 0) {
      setFilteredOptions(dropdownOption)
      setNoResults(false)
    }
  }, [dropdownOption])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])
  useEffect(() => {
    if (!isOpen && multipleSelection) {
      handleInputChange('')
    }
  }, [isOpen])

  useEffect(() => {
    if (multipleSelection) {
      const selectedValue = dropdownSelectedOption.map(item => ({
        ...item,
        id: String(item.id),
        label: item.label,
      }))
      setChipValues(selectedValue)
    }
  }, [dropdownSelectedOption, multipleSelection])

  const handleInputChange = newVal => {
    onChange(newVal) // Pass typed value to parent
    if (newVal) {
      const filtered = dropdownOption.filter(item =>
        item.label.toLowerCase().includes(newVal.toLowerCase())
      )
      setFilteredOptions(filtered)
      setNoResults(filtered.length === 0)
    } else {
      setFilteredOptions(dropdownOption)
      setNoResults(false)
    }

    if (newVal === '') {
      setIsClearing(true)
    }

    if (newVal === '' && !multipleSelection) {
      onSelectDropdownItem([])
    }
  }

  const handleDropdownSelect = selectedItems => {
    if (isClearing) {
      setIsClearing(false) // Stop clearing once selection is changed
      if (!isOpen) return // Prevent infinite re-rendering when dropdown is closed
    }

    onSelectDropdownItem(selectedItems)

    if (!multipleSelection) {
      setFilteredOptions(dropdownOption)
      const selectedValue = selectedItems[0]?.label || ''
      onChange(selectedValue)
    } else {
      const selectedValue = selectedItems.map(item => ({
        ...item,
        id: String(item.id),
        label: item.label,
      }))
      setChipValues(selectedValue)
    }
  }

  const handleSelectAll = () => {
    if (dropdownSelectedOption.length === filteredOptions.length) {
      onSelectDropdownItem([])
    } else {
      onSelectDropdownItem(filteredOptions)
    }
  }

  const isAllSelected =
    filteredOptions.length > 0 && dropdownSelectedOption.length === filteredOptions.length

  const handleOptionSelect = option => {
    if (multipleSelection) {
      const isSelected = dropdownSelectedOption.some(item => item.id === option.id)
      const newSelection = isSelected
        ? dropdownSelectedOption.filter(item => item.id !== option.id)
        : [...dropdownSelectedOption, option]
      onSelectDropdownItem(newSelection)
    } else {
      onSelectDropdownItem([option])
      setIsOpen(false)
    }
  }

  const handleClearChipSelection = () => {
    setIsClearing(true)
    setChipValues([])
    onSelectDropdownItem([])
    setFilteredOptions(dropdownOption)
    handleInputChange('')
    setIsOpen(false)
  }

  const onInputClick = () => {
    setIsOpen(true)
  }

  const suffixIcon = <Icon name="chevron-down" style="solid" size="small" disabled={disabled} />

  const DropdownTrigger = (
    <div>
      <Input
        id={id}
        name={name}
        placeholder={
          multipleSelection && chipValues?.length > 0
            ? ''
            : dropdownSelectedOption.length > 0
              ? ''
              : placeholder || 'Search'
        }
        suffixIcon={suffixIcon}
        label={label}
        labelInfoText={labelInfoText}
        variant={variant}
        size={size}
        required={required}
        disabled={disabled}
        infoText={infoText}
        errorMessage={errorMessage}
        captionText={captionText}
        value={disableSearch ? '' : value}
        onChange={disableSearch ? () => {} : handleInputChange}
        onClick={onInputClick}
        chipInputMultiple={multipleSelection}
        chipValues={multipleSelection ? chipValues : undefined}
        setChipValues={setChipValues}
        onClearChips={handleClearChipSelection}
        fullWidth={fullWidth}
        readOnly={disableSearch}
      />
    </div>
  )

  return (
    <div
      ref={dropdownRef}
      className={clsx('relative max-phone:w-full', fullWidth ? 'w-full' : 'w-[350px]')}
      style={!fullWidth && style?.width ? { width: style.width } : undefined}
    >
      {DropdownTrigger}
      {isOpen && (
        <div className="overflow-y-auto box-border absolute top-full left-0 z-1000 w-full mt-1 max-h-[282px] p-px border border-grey-80 rounded bg-white">
          {isLoading ? (
            <div className="flex items-center justify-center box-border py-6">
              <Loader size="small" />
            </div>
          ) : noResults ? (
            <div className="flex items-center justify-center box-border py-6">
              <div className="flex flex-col items-center justify-center">
                <div className="text-grey-50 type-meta-medium">No results found</div>
              </div>
            </div>
          ) : (
            <ul className="m-0 border-0 list-none">
              {allowSelectAll && multipleSelection && filteredOptions.length > 0 && (
                <li
                  key="all"
                  className="box-border w-full text-grey-50 hover:cursor-pointer hover:bg-grey-95"
                >
                  <div className="flex items-center py-1.5 mx-3" onClick={handleSelectAll}>
                    <div className="mr-3">
                      <Checkbox
                        id="select-all"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                    <div className="grow overflow-hidden">
                      <div className="type-body-2 truncate max-w-full text-left">All Options</div>
                    </div>
                  </div>
                </li>
              )}
              {filteredOptions.map(option => (
                <li
                  key={option.id}
                  className="box-border w-full text-grey-50 hover:cursor-pointer hover:bg-grey-95"
                >
                  <div
                    className="flex items-center py-1.5 mx-3"
                    onClick={() => handleOptionSelect(option)}
                  >
                    {multipleSelection && (
                      <div className="mr-3">
                        <Checkbox
                          id={option.id.toString()}
                          checked={dropdownSelectedOption.some(item => item.id === option.id)}
                          onChange={() => handleOptionSelect(option)}
                        />
                      </div>
                    )}
                    <div className="grow overflow-hidden">
                      <div className="type-body-2 truncate max-w-full text-left">
                        {option.label}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export default InputDropdown

InputDropdown.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  labelInfoText: PropTypes.string,
  variant: PropTypes.oneOf(['outlined', 'filled', 'underlined']),
  size: PropTypes.oneOf(['default', 'small']),
  fullWidth: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  infoText: PropTypes.string,
  errorMessage: PropTypes.string,
  captionText: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  dropdownOption: PropTypes.array,
  dropdownSelectedOption: PropTypes.array,
  onSelectDropdownItem: PropTypes.func,
  multipleSelection: PropTypes.bool,
  allowSelectAll: PropTypes.bool,
  isLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  showTooltipInfo: PropTypes.bool,
  dataTestId: PropTypes.string,
  style: PropTypes.object,
  disableSearch: PropTypes.bool,
}
